/**
 * AiExtractedInsuranceSuggestion - Displays AI-extracted insurance suggestion (read-only)
 */

import React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
} from '@roger/ui-components';
import { HelpCircle } from 'lucide-react';
import { AIReasoningButton, type FieldExplanation } from './AIReasoningButton';

const PRIMARY_INSURANCE_FIELD_ID = 'primary_insurance';
const MEDICARE_EXPLANATION_KEYS = [
  'medicare_beneficiary_id',
  'medicare_beneficiary_identifier_mbi',
  'medicare_number',
] as const;
const DEFAULT_PRIMARY_INSURANCE_EXPLANATION_KEYS = [
  'insurance_payer',
  ...MEDICARE_EXPLANATION_KEYS,
] as const;
const MEDICARE_PRIMARY_INSURANCE_EXPLANATION_KEYS = [
  ...MEDICARE_EXPLANATION_KEYS,
  'insurance_payer',
] as const;

const getPrimaryInsuranceExplanationKeys = (
  primaryInsurance?: string | null
): readonly string[] => {
  if (primaryInsurance?.toLowerCase().startsWith('medicare')) {
    return MEDICARE_PRIMARY_INSURANCE_EXPLANATION_KEYS;
  }

  return DEFAULT_PRIMARY_INSURANCE_EXPLANATION_KEYS;
};

const normalizeFieldExplanation = (value: unknown): FieldExplanation | null => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return { explanation: value };
  }

  if (typeof value === 'object') {
    return value as FieldExplanation;
  }

  return null;
};

const getPrimaryInsuranceFieldExplanation = (
  fieldExplanation?: unknown,
  fieldsExplanations?: Record<string, unknown> | null,
  primaryInsurance?: string | null
): FieldExplanation | null => {
  const directExplanation = normalizeFieldExplanation(fieldExplanation);
  if (directExplanation) {
    return directExplanation;
  }

  if (!fieldsExplanations) {
    return null;
  }

  for (const key of getPrimaryInsuranceExplanationKeys(primaryInsurance)) {
    const explanation = normalizeFieldExplanation(fieldsExplanations[key]);
    if (explanation) {
      return explanation;
    }
  }

  return null;
};

export interface AiExtractedInsuranceSuggestionProps {
  /** The AI-extracted suggestion value */
  value: string | null | undefined;
  /** Custom label text (defaults to "AI-extracted suggestion") */
  label?: string;
  /** Custom help text for the popover */
  helpText?: string;
  /** Whether the current user can see AI extraction reasoning */
  showAIExplanations?: boolean;
  /** Explanation payload for the extracted primary insurance suggestion */
  fieldExplanation?: unknown;
  /** All extracted field explanations; used to resolve derived insurance fields */
  fieldsExplanations?: Record<string, unknown> | null;
  /** Upload name -> display name for intake documents */
  documentNameDict?: Record<string, string>;
}

export const AiExtractedInsuranceSuggestion: React.FC<
  AiExtractedInsuranceSuggestionProps
> = ({
  value,
  label = 'AI-extracted suggestion',
  helpText = 'This is the insurance we extracted from the uploaded documents. Please double check and select the correct insurance configured for your agency in Kinnser above.',
  showAIExplanations = false,
  fieldExplanation,
  fieldsExplanations,
  documentNameDict,
}) => {
  if (!value) {
    return null;
  }

  const explanationData = getPrimaryInsuranceFieldExplanation(
    fieldExplanation,
    fieldsExplanations,
    value
  );

  return (
    <div className='text-xs text-gray-500 mt-1 flex items-center gap-1'>
      <span>
        {label}: {value}
      </span>
      <AIReasoningButton
        fieldId={PRIMARY_INSURANCE_FIELD_ID}
        fieldLabel='Primary Insurance'
        showAIExplanations={showAIExplanations}
        explanationData={explanationData}
        documentNameDict={documentNameDict}
      />
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='h-4 w-4 p-0 text-gray-400 hover:text-gray-600'
          >
            <HelpCircle className='h-3 w-3' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-80' align='start'>
          <div className='text-sm text-gray-700'>{helpText}</div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
